import InlineAudienceExport from '../../pages/audienceManager/export/InlineAudienceExport';
import React, { useMemo, useState } from 'react';
import numberFormatter from '../../utils/formatNumber';
import {
  AUDIENCE_LAL_MODAL_TYPE,
  AudiencePlatformLabel,
  MODEL_SCORING_TYPE,
  type ModelScoringTypeValue,
} from '../../constants/index';
import { Alert, theme } from '@attain-sre/attain-design-system';
import { Box, Slider, Tooltip, Typography } from '@mui/material';
import { Info } from '@phosphor-icons/react';
import {
  MODEL_SCORING_TYPE_LABEL,
  SELECT_AUDIENCE_SIZE_INFORMATION_TEXT,
  SELECT_AUDIENCE_SIZE_PRICE_OPTIMIZATION_INFORMATION_TEXT,
} from '../../content';
import {
  ModelScoringModeToggle,
  ModelScoringValueInsights,
} from '../../components/ModelScoringMode';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  calculateMinAndMaxAudienceCount,
  groupModeledAudienceCountByIdType,
} from '../../helper/helper';
import { useFlags } from 'launchdarkly-react-client-sdk';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(3),
      padding: '16px 16px 0px 16px',
    },
    sliderBox: {
      border: '1px solid #ccc',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      flex: 3,
      background: 'white',
    },
    header: {
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontWeight: 600,
      fontSize: 22,
      lineHeight: '32px',
    },
    body: {
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
      marginTop: '4px',
    },
    slider: {
      width: '96% !important',
      marginLeft: '16px !important',
      maxWidth: '1060px',
      color: '#413762 !important',
      '& > .MuiSlider-markLabel': {
        fontFamily: 'Calibre-Regular,sans-serif',
      },
    },
    audienceDefinitionBox: {
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #ccc',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      flex: 1,
      background: 'white',
    },
    progressBarContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(2),
    },
    progressBar: {
      flexGrow: 1,
      position: 'relative',
      height: 8,
      borderRadius: 5,
      backgroundColor: '#e0e0e0',
    },
    progressIndicator: {
      position: 'absolute',
      height: '100%',
      borderRadius: 5,
    },
    labelText: {
      fontFamily: 'Calibre-Regular,sans-serif',
    },
    divider: {
      width: '100%',
      color: '#ccc',
      opacity: '0.2',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    subText: {
      color: '#685d6f',
      fontSize: 12,
      marginRight: 5,
    },
    listItemContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
      marginTop: '1px',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'end',
      gap: 4,
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    modelScoringLabel: {
      display: 'block',
      fontFamily: 'Calibre-Semibold,sans-serif',
      fontSize: 16,
      marginBottom: 8,
    },
  })
);

interface ExportYourAudienceProps {
  currentAudience: any;
  lalModelPrecision: number;
  setLalModelPrecision: (value: number) => void;
  audienceCountTotals: any;
  audienceType: string;
  isModelingSkipped: boolean;
}

const ExportYourAudience: React.FC<ExportYourAudienceProps> = ({
  currentAudience,
  lalModelPrecision,
  setLalModelPrecision,
  audienceCountTotals,
  audienceType,
  isModelingSkipped,
}) => {
  const classes = useStyles();
  const {
    uiAudienceManagerModelScoringModeVisible = false,
  } = useFlags();

  const [selectedModelScoringType, setSelectedModelScoringType] =
    useState<ModelScoringTypeValue>(MODEL_SCORING_TYPE.LAL);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const next =
      typeof newValue === 'number'
        ? newValue
        : newValue[0] ?? lalModelPrecision;
    setLalModelPrecision(next);
  };

  const marks = Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: `${i}%`,
  }));

  const targetAudience = useMemo(() => {
    if (lalModelPrecision <= 3) {
      return 'specific.';
    } else if (lalModelPrecision <= 7) {
      return 'moderate.';
    } else {
      return 'fairly broad.';
    }
  }, [lalModelPrecision]);

  const progressIndicatorColor = useMemo(() => {
    if (lalModelPrecision <= 3) {
      return '#F288D7';
    } else if (lalModelPrecision <= 7) {
      return '#CAD31D';
    } else {
      return '#FF8266';
    }
  }, [lalModelPrecision]);

  const lalAudienceCount = groupModeledAudienceCountByIdType(
    currentAudience?.lalAudienceCount
  );

  const { minimumAudienceCount, maximumAudienceCount } =
    calculateMinAndMaxAudienceCount(lalAudienceCount);

  const RenderEstimatedAudienceSize = ({
    groupedData,
  }: {
    groupedData: Record<string, Array<{ audienceCount: number }>>;
  }) => {
    const hasValues = Object.values(groupedData).some(arr => arr.length > 0);
    if (!hasValues && lalModelPrecision) {
      return null;
    }

    return (
      <div>
        {AudiencePlatformLabel.map(item => {
          const audienceCount = groupedData[item.TYPE]
            ? groupedData[item.TYPE][lalModelPrecision]?.audienceCount
            : null;
          const lowerBoundRange = audienceCount
            ? Math.round(audienceCount * 0.9)
            : 0;
          const upperBoundRange = audienceCount
            ? Math.round(audienceCount * 1.1)
            : 0;
          return (
            <div key={item.PRIMARY_TEXT} className={classes.listItemContainer}>
              <div className={classes.leftSection}>
                <b>{item.PRIMARY_TEXT}</b>
                <span className={classes.subText}>{item.SECONDARY_TEXT}</span>
              </div>
              <div className={classes.rightSection}>
                {numberFormatter(lowerBoundRange, 1)}
                {'  -  '}
                {numberFormatter(upperBoundRange, 1)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const isSelectAudienceVisible =
    currentAudience?.lalModelType === AUDIENCE_LAL_MODAL_TYPE.ATTAIN &&
    currentAudience?.lalModelStatus === 'COMPLETED' &&
    isModelingSkipped;

  const resolvedModelScoringType =
    isSelectAudienceVisible && uiAudienceManagerModelScoringModeVisible
      ? selectedModelScoringType
      : MODEL_SCORING_TYPE.LAL;

  const audienceSizeInformationText = useMemo(() => {
    if (
      uiAudienceManagerModelScoringModeVisible &&
      selectedModelScoringType === MODEL_SCORING_TYPE.CPM_V1
    ) {
      return SELECT_AUDIENCE_SIZE_PRICE_OPTIMIZATION_INFORMATION_TEXT;
    }
    return SELECT_AUDIENCE_SIZE_INFORMATION_TEXT;
  }, [uiAudienceManagerModelScoringModeVisible, selectedModelScoringType]);

  return (
    <>
      {isSelectAudienceVisible && (
        <Box className={classes.container}>
          {/* Audience Size Slider */}
          <Box className={classes.sliderBox}>
            <span className={classes.header}>Select Audience Size</span>
            {uiAudienceManagerModelScoringModeVisible && (
              <ModelScoringModeToggle
                value={selectedModelScoringType}
                onChange={setSelectedModelScoringType}
              />
            )}
            <div style={{ display: 'flex', marginTop: 8 }}>
              <Alert
                severity='info'
                style={{
                  marginTop: theme.spacing.xs,
                  marginBottom: theme.spacing.xs,
                }}
              >
                {audienceSizeInformationText}
              </Alert>
            </div>

            <Slider
              className={classes.slider}
              value={lalModelPrecision}
              onChange={handleSliderChange}
              step={1}
              valueLabelDisplay='auto'
              marks={marks}
              max={10}
            />
            {uiAudienceManagerModelScoringModeVisible &&
              selectedModelScoringType === MODEL_SCORING_TYPE.CPM_V1 && (
                <ModelScoringValueInsights
                  valueCompositions={currentAudience?.valueCompositions}
                  lalModelPrecision={lalModelPrecision}
                />
              )}
          </Box>

          {/* Audience Definition */}
          <Box className={classes.audienceDefinitionBox}>
            <span className={classes.header}>
              Audience Definition{' '}
              <Tooltip title='Audience selection indicator'>
                <span>
                  <Info size={18} weight='fill' />
                </span>
              </Tooltip>
            </span>
            <span className={classes.body}>
              Your audience selection is {targetAudience}
            </span>

            {/* Progress Indicator */}
            <Box className={classes.progressBarContainer}>
              <Typography
                variant='body2'
                className={classes.labelText}
                style={{ marginRight: '8px' }}
              >
                Specific
              </Typography>
              <Box className={classes.progressBar}>
                <Box
                  className={classes.progressIndicator}
                  style={{
                    width: `${lalModelPrecision * 10}%`,
                    backgroundColor: progressIndicatorColor,
                  }}
                />
              </Box>
              <Typography
                variant='body2'
                className={classes.labelText}
                style={{ marginLeft: '8px' }}
              >
                Broad
              </Typography>
            </Box>
            {minimumAudienceCount && maximumAudienceCount && (
              <>
                <hr className={classes.divider} />
              </>
            )}
            <RenderEstimatedAudienceSize groupedData={lalAudienceCount} />
          </Box>
        </Box>
      )}

      <InlineAudienceExport
        lalModelType='attain'
        lalModelPrecision={lalModelPrecision}
        audienceName={currentAudience?.name}
        audienceCountTotals={audienceCountTotals}
        audienceType={audienceType}
        isSelectAudienceVisible={isSelectAudienceVisible}
        modelScoringType={resolvedModelScoringType}
      />
    </>
  );
};

export default ExportYourAudience;
